import { bytesToB64u, b64uToBytes } from './base64url';

/**
 * `hints` is a top-level field on the WebAuthn creation/request options
 * (WebAuthn L3), but our TS DOM lib doesn't ship it yet. Declare-merge the
 * precise W3C union so the ceremony can request authenticator hints without
 * casts. Drop this augmentation once the workspace lib.dom.d.ts ships `hints`.
 */
declare global {
  interface PublicKeyCredentialCreationOptions {
    hints?: ('client-device' | 'hybrid' | 'security-key')[];
  }
  interface PublicKeyCredentialRequestOptions {
    hints?: ('client-device' | 'hybrid' | 'security-key')[];
  }
}

/**
 * Adapter that runs WebAuthn ceremonies. Production implementations call
 * `navigator.credentials.create/get`; the test implementation in
 * `SoftAuthenticator` produces deterministic results without browser APIs.
 *
 * Spec §3.7. The `prfOutput` field is the result of the WebAuthn PRF extension
 * — present when the authenticator supports PRF, absent otherwise.
 */
export interface CeremonyAdapter {
  register(opts: RegisterOptions): Promise<RegisterResult>;
  login(opts: LoginOptions): Promise<LoginResult>;
}

export interface RegisterOptions {
  rpId: string;
  challenge: Uint8Array;
  userHandle: Uint8Array;
  /** 32-byte PRF input (typically `PRF_INPUT_SEED`). */
  prfInput: Uint8Array;
  /**
   * Legacy single label. Used as a fallback for both `user.displayName` and
   * `user.name` when `name`/`email` are not supplied. Prefer `name` + `email`.
   */
  label?: string;
  /** Friendly account name → WebAuthn `user.displayName` (e.g. "Alex Müller"). */
  name?: string;
  /**
   * Account identifier → WebAuthn `user.name` (e.g. "alex@example.com"). This is
   * the field most OS/browser passkey managers surface to distinguish credentials.
   */
  email?: string;
}

export interface RegisterResult {
  credentialId: string; // base64url
  clientDataJSON: string; // base64url
  attestationObject: string; // base64url
  transports: string[];
  /** Present iff PRF extension worked. */
  prfOutput?: Uint8Array;
}

export interface LoginOptions {
  rpId: string;
  challenge: Uint8Array;
  /** Optional: if absent, the authenticator picks a credential. Present from us. */
  credentialId?: string;
  prfInput: Uint8Array;
}

export interface LoginResult {
  credentialId: string;
  clientDataJSON: string;
  authenticatorData: string;
  signature: string;
  prfOutput?: Uint8Array;
}

export interface WebAuthnCeremonyAdapterOptions {
  /**
   * Override for the WebAuthn credentials manager. Defaults to
   * `globalThis.navigator?.credentials`. Tests inject a mock.
   */
  credentials?: CredentialsContainer;
}

/**
 * Authenticator selection `hints` (WebAuthn L3, Chrome/Edge/Safari 18+).
 *
 * These bias the browser's passkey picker toward authenticators that
 * reliably support the PRF extension — platform authenticators (Touch ID /
 * Windows Hello / Android), phone passkeys (hybrid), and security keys.
 *
 * Hints are *preferences, not filters*: a software password-manager passkey
 * (e.g. Bitwarden, 1Password) still appears in the picker, just de-prioritized.
 * On a machine with Touch ID enrolled this makes "This device" the default
 * choice, so users no longer land on Bitwarden's passkey (which can't satisfy
 * PRF and fails the ceremony with `prf-unsupported`). The post-ceremony
 * `prfOutput` check remains the real capability gate.
 */
const PRF_CAPABLE_HINTS = ['client-device', 'hybrid', 'security-key'] as const;

/**
 * Production WebAuthn ceremony adapter. Calls `navigator.credentials.create/get`
 * with the PRF extension. Spec §3.7.
 *
 * - `register`: requires a platform authenticator, user verification, Ed25519
 *   (`alg: -8`), and the PRF extension with the wallet's input seed.
 * - `login`: allows a specific credentialId if known; otherwise lets the
 *   authenticator pick (resident key).
 *
 * Returned ArrayBuffers are base64url-encoded for API transport.
 */
export class WebAuthnCeremonyAdapter implements CeremonyAdapter {
  private readonly credentials?: CredentialsContainer;

  constructor(opts: WebAuthnCeremonyAdapterOptions = {}) {
    this.credentials = opts.credentials ?? (globalThis as any).navigator?.credentials;
  }

  async register(opts: RegisterOptions): Promise<RegisterResult> {
    if (!this.credentials) {
      throw new Error('WebAuthnCeremonyAdapter: navigator.credentials is unavailable');
    }
    const cred = (await this.credentials.create({
      publicKey: {
        challenge: opts.challenge,
        rp: { id: opts.rpId, name: opts.rpId },
        user: {
          id: opts.userHandle,
          // WebAuthn convention: `name` is the account identifier (email), shown
          // by passkey managers; `displayName` is the friendly name. Fall back to
          // the legacy single `label`, then the product defaults.
          name: opts.email ?? opts.label ?? 'scp-wallet',
          displayName: opts.name ?? opts.label ?? 'SCP Wallet',
        },
        pubKeyCredParams: [
          { alg: -8, type: 'public-key' }, // EdDSA (Ed25519 security keys) — preferred
          { alg: -7, type: 'public-key' }, // ES256 (Apple/Windows/Android platform authenticators)
        ],
        authenticatorSelection: {
          // No `authenticatorAttachment`: allow platform authenticators
          // (Touch ID / Windows Hello), roaming security keys, and phone
          // passkeys via hybrid — PRF works across all of them, and the
          // pubKeyCredParams above explicitly prefer Ed25519 security keys.
          // The post-ceremony PRF result is the real capability gate.
          userVerification: 'required',
          residentKey: 'preferred',
        },
        // Bias the picker toward PRF-capable authenticators so the user
        // doesn't default onto a password-manager passkey. See PRF_CAPABLE_HINTS.
        hints: [...PRF_CAPABLE_HINTS],
        timeout: 60_000,
        extensions: { prf: { eval: { first: opts.prfInput } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
    if (!cred) {
      throw new Error('WebAuthnCeremonyAdapter.register: credentials.create returned null');
    }

    const response = cred.response as AuthenticatorAttestationResponse;
    const transports = typeof response.getTransports === 'function' ? response.getTransports() : [];
    const ext = cred.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } };
    const prfFirst = ext?.prf?.results?.first;

    return {
      credentialId: cred.id,
      clientDataJSON: bytesToB64u(response.clientDataJSON),
      attestationObject: bytesToB64u(response.attestationObject),
      transports,
      prfOutput: prfFirst ? new Uint8Array(prfFirst) : undefined,
    };
  }

  async login(opts: LoginOptions): Promise<LoginResult> {
    if (!this.credentials) {
      throw new Error('WebAuthnCeremonyAdapter: navigator.credentials is unavailable');
    }
    const cred = (await this.credentials.get({
      publicKey: {
        challenge: opts.challenge,
        rpId: opts.rpId,
        allowCredentials: opts.credentialId ? [{ id: b64uToBytes(opts.credentialId), type: 'public-key' }] : undefined,
        userVerification: 'required',
        // Bias first-login-on-device (no allowCredentials) toward PRF-capable
        // authenticators, mirroring register. See PRF_CAPABLE_HINTS.
        hints: [...PRF_CAPABLE_HINTS],
        timeout: 60_000,
        extensions: { prf: { eval: { first: opts.prfInput } } } as AuthenticationExtensionsClientInputs,
      },
    })) as PublicKeyCredential | null;
    if (!cred) {
      throw new Error('WebAuthnCeremonyAdapter.login: credentials.get returned null');
    }

    const response = cred.response as AuthenticatorAssertionResponse;
    const ext = cred.getClientExtensionResults() as { prf?: { results?: { first?: ArrayBuffer } } };
    const prfFirst = ext?.prf?.results?.first;

    return {
      credentialId: cred.id,
      clientDataJSON: bytesToB64u(response.clientDataJSON),
      authenticatorData: bytesToB64u(response.authenticatorData),
      signature: bytesToB64u(response.signature),
      prfOutput: prfFirst ? new Uint8Array(prfFirst) : undefined,
    };
  }
}
